import type { ReactNode } from "react";
import { CitationLink, type ResolvedCitationData } from "./citation-link";
import { CitationCard } from "./citation-card";
import { useCitationMode } from "./use-citation-mode";

// Matches both formats:
// 1. [rule-<uuid>], [fact-<uuid>], [chunk-<uuid>]  (structured output)
// 2. [rule-001], [fact-003], [chunk-012]             (LLM short labels in grounding text)
const CITATION_RE =
  /\[(rule|fact|chunk)-((?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})|(?:\d{1,4}))\]/gi;

interface CitationTextProps {
  text: string;
  /** Map from citation key (UUID or short label like "rule-001") to resolved data */
  citations: Map<string, ResolvedCitationData>;
  onOpenPanel: (sourceType: string, sourceId: string) => void;
  className?: string;
}

export function CitationText({
  text,
  citations,
  onOpenPanel,
  className,
}: CitationTextProps) {
  const [mode] = useCitationMode();

  if (!text) return null;

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Reset lastIndex for global regex
  CITATION_RE.lastIndex = 0;

  while ((match = CITATION_RE.exec(text)) !== null) {
    const [fullMatch, sourceType, idPart] = match;
    const beforeText = text.slice(lastIndex, match.index);

    if (beforeText) {
      parts.push(
        <span key={`text-${lastIndex}`} className="whitespace-pre-wrap">
          {beforeText}
        </span>,
      );
    }

    // Try lookup by full key ("rule-001") then by just the id part
    const fullKey = `${sourceType!.toLowerCase()}-${idPart}`;
    const resolved = citations.get(fullKey) ?? citations.get(idPart!);
    const resolvedSourceId = resolved?.sourceId ?? idPart!;
    const resolvedSourceType =
      resolved?.sourceType ?? sourceType!.toLowerCase();

    if (mode === "compact") {
      parts.push(
        <CitationLink
          key={`cite-${match.index}`}
          sourceId={resolvedSourceId}
          sourceType={resolvedSourceType}
          resolved={resolved}
          onClick={onOpenPanel}
        />,
      );
    } else {
      // Annotated mode: inline link + expanded card
      parts.push(
        <span key={`cite-${match.index}`}>
          <CitationLink
            sourceId={resolvedSourceId}
            sourceType={resolvedSourceType}
            resolved={resolved}
            onClick={onOpenPanel}
          />
          {resolved && (
            <CitationCard resolved={resolved} onViewSource={onOpenPanel} />
          )}
        </span>,
      );
    }

    lastIndex = match.index + fullMatch.length;
  }

  // Remaining text after last match
  if (lastIndex < text.length) {
    parts.push(
      <span key={`text-${lastIndex}`} className="whitespace-pre-wrap">
        {text.slice(lastIndex)}
      </span>,
    );
  }

  // No citations found — render as plain text
  if (parts.length === 0) {
    return <span className={className}>{text}</span>;
  }

  return <span className={className}>{parts}</span>;
}
